import configs from "../configs";
import { getAuth0Token } from "../ui/utils";

const subscriptionApiUrl = () => configs.SUBSCRIPTION_API_URL || configs.CUSTOM_API_URL;

export function normalizeEffectiveEntitlements(payload) {
  const limit = key => {
    const value = payload?.[key];
    if (value === null) return null;
    if (!Number.isSafeInteger(value) || value < 0) throw new Error("entitlement_authority_unavailable");
    return value;
  };
  const string = key => {
    const value = payload?.[key];
    if (typeof value !== "string" || value.length === 0) throw new Error("entitlement_authority_unavailable");
    return value;
  };
  if (
    payload?.status !== "ready" ||
    !Number.isSafeInteger(payload?.identity_user_id) ||
    typeof payload?.limits_enabled !== "boolean"
  ) {
    throw new Error("entitlement_authority_unavailable");
  }
  return {
    identityUserId: payload.identity_user_id,
    status: "ready",
    limitsEnabled: payload.limits_enabled,
    maxCcu: limit("max_ccu"),
    maxLobbyGuests: limit("max_lobby_guests"),
    maxMicrophones: limit("max_microphones"),
    maxCameras: limit("max_cameras"),
    maxScreenShares: limit("max_screen_shares"),
    maxStorageMb: limit("max_storage"),
    maxProjects: limit("max_projects"),
    maxWorkspaces: limit("max_workspaces"),
    maxMembers: limit("max_members"),
    maxInstantAccesses: limit("max_instant_accesses"),
    policyVersion: string("policy_version"),
    sourceVersion: string("source_version"),
    projectionVersion: string("projection_version"),
    computedAt: string("computed_at"),
    usage: payload.usage && typeof payload.usage === "object" ? payload.usage : {}
  };
}

export function normalizeSubscriptionStatus(payload) {
  if (Object.prototype.hasOwnProperty.call(payload || {}, "subscription")) {
    if (!payload.subscription || typeof payload.subscription !== "object") return null;
    return {
      ...payload.subscription,
      status: payload.subscription.access_status ?? payload.subscription.status ?? null,
      limits_enabled: payload.limits_enabled,
      policy_version: payload.policy_version,
      source_version: payload.source_version,
      projection_version: payload.projection_version
    };
  }
  const sanitized = { ...(payload || {}) };
  delete sanitized.product_data;
  return sanitized;
}

function normalizeSubscriptions(payload) {
  const normalize = (value, sourceKind) => ({
    ...value,
    status: value.access_status ?? value.status ?? "active",
    source_kind: value.source_kind ?? sourceKind
  });
  return {
    purchased: Array.isArray(payload?.purchased) ? payload.purchased.map(value => normalize(value, "purchased")) : [],
    direct_templates: Array.isArray(payload?.direct_templates)
      ? payload.direct_templates.map(value => normalize(value, "direct_template"))
      : [],
    group_templates: Array.isArray(payload?.group_templates)
      ? payload.group_templates.map(value => normalize(value, "group_template"))
      : []
  };
}

export default class SubscriptionsService {
  async validateSession(fetch, { sessionId }) {
    const token = await getAuth0Token();

    const url = `${subscriptionApiUrl()}/subscriptions/verify?session_id=${sessionId}`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        Accept: "application/json"
      }
    });

    if (!response.ok) {
      throw new Error(`Network Error: ${response.statusText}`);
    }

    const json = await response.json();

    return json;
  }

  async getSubscriptions(fetch, { queryParams }) {
    const token = await getAuth0Token();
    let url = `${subscriptionApiUrl()}/subscriptions`;

    if (queryParams) {
      url = `${subscriptionApiUrl()}/subscriptions?${queryParams}`;
    }

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        Accept: "application/json"
      }
    });

    if (!response.ok) throw new Error(`Network Error: ${response.statusText}`);
    return normalizeSubscriptions(await response.json());
  }

  async getSubscriptionStatus(fetch, { queryParams }) {
    const token = await getAuth0Token();
    let url = `${subscriptionApiUrl()}/subscriptions/status`;

    if (queryParams) {
      url = `${subscriptionApiUrl()}/subscriptions/status?${queryParams}`;
    }

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        Accept: "application/json"
      }
    });

    if (!response.ok) throw new Error(`Network Error: ${response.statusText}`);
    return normalizeSubscriptionStatus(await response.json());
  }

  async getEffectiveEntitlements(fetch) {
    const token = await getAuth0Token();
    const response = await fetch(`${subscriptionApiUrl()}/limits`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        Accept: "application/json"
      }
    });
    if (!response.ok) throw new Error("entitlement_authority_unavailable");
    return normalizeEffectiveEntitlements(await response.json());
  }

  async getCharges(fetch, { root }) {
    const token = await getAuth0Token();
    let url = `${configs.CUSTOM_API_URL}/charges`;

    if (root) {
      url = `${configs.CUSTOM_API_URL}/charges?root=${root}`;
    }

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json"
        }
      });

      if (!response.ok) {
        throw new Error(`Network Error: ${response.statusText}`);
      }

      const json = await response.json();

      return json;
    } catch (e) {
      console.error(e);
      return e.message;
    }
  }

  async getSubscriptionCharges(fetch, { id }) {
    const token = await getAuth0Token();
    const url = `${configs.CUSTOM_API_URL}/subscriptions/${id}/charges`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        Accept: "application/json"
      }
    });

    if (!response.ok) {
      throw new Error(`Network Error: ${response.statusText}`);
    }

    const json = await response.json();

    return json;
  }

  async cancelSubscription(fetch, { id }) {
    const token = await getAuth0Token();
    const url = `${subscriptionApiUrl()}/subscriptions/${id}/cancel`;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json"
        }
      });

      if (!response.ok) {
        throw new Error(`Network Error: ${response.statusText}`);
      }

      const json = await response.json();
      return json;
    } catch (e) {
      console.error(e);
      return e.message;
    }
  }

  async resumeSubscription(fetch, { id }) {
    const token = await getAuth0Token();
    const url = `${subscriptionApiUrl()}/subscriptions/${id}/resume`;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json"
        }
      });

      if (!response.ok) {
        throw new Error(`Network Error: ${response.statusText}`);
      }
      const json = await response.json();
      return json;
    } catch (e) {
      console.error(e);
      return e.message;
    }
  }
}
